import { takeLatest, put, call, select, getContext } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

let recruitmentRetentionCancelSource: ReturnType<typeof axios.CancelToken.source> | null = null;
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { getActiveRecruitersByLoggedInUserId } from 'app/services/CandidateServices/CandidateServices';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackPromise } from 'react-promise-tracker';
import { getFiscalYearStartFromDate } from 'utils/dates/dateExtraction';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import { myCandidatesActions, IRecruiterOption } from './MyCandidates.redux';
import {
  computeBarChartData,
  computeQuickviewMetrics,
} from './recruitmentRetentionHelper';
import {
  IRecruitmentRetentionPlacement,
  IRetentionBarChartMonth,
  IQuickviewMetric,
} from 'app/models/MyCandidates/RecruitmentRetention';

const TrackWrapper = (fn, ...args) => trackPromise(fn(...args), 'MyCandidates-recruitment-retention');
const TrackRecruiters = (fn, ...args) => trackPromise(fn(...args), 'MyCandidates-recruiter-options');

function buildRecruiterOptions(recruiters: any[], userInfo: { employeeId: number; firstName?: string; lastName?: string }): IRecruiterOption[] {
  const currentUser = {
    id: userInfo.employeeId,
    name: Concatenate([userInfo.firstName ?? '', userInfo.lastName ?? ''], ' '),
    value: userInfo.employeeId,
    object: { userId: userInfo.employeeId, firstName: userInfo.firstName, lastName: userInfo.lastName },
  };
  const others = (recruiters ?? [])
    .filter((r: any) => r.userId !== userInfo.employeeId)
    .map((r: any) => ({
      id: r.userId,
      name: Concatenate([r.firstName ?? '', r.lastName ?? ''], ' '),
      value: r.userId,
      object: { userId: r.userId, firstName: r.firstName, lastName: r.lastName },
    }));
  return [currentUser, ...others];
}

export function* fetchRecruiterOptions(action: PayloadAction<{ userId: number }>) {
  try {
    const userState = yield select(selectUser);
    const appUser = userState?.userInfo;
    const response = yield call(TrackRecruiters, getActiveRecruitersByLoggedInUserId, action.payload.userId, 'R');
    if (httpSuccess(response?.status) && appUser) {
      const recruiters = response?.data ?? [];
      const options = buildRecruiterOptions(recruiters, appUser);
      yield put(myCandidatesActions.setRecruiterOptions(options));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchRecruiterOptions',
        area: 'src/app/components/RecruiterWorkDesk/MyCandidatesDesk/MyCandidates.saga.ts',
      },
    });
  }
}

function buildCandidateName(raw: any): string {
  const first = raw?.firstname ?? raw?.firstName ?? raw?.first ?? '';
  const last = raw?.lastname ?? raw?.lastName ?? raw?.last ?? '';
  const existing = raw?.candidateName ?? raw?.CandidateName ?? '';
  if (existing) return existing;
  return `${first || ''} ${last || ''}`.trim() || '-';
}

function buildSkillset(raw: any): string {
  const discipline = raw?.discipline ?? raw?.Discipline ?? '';
  const specialty = raw?.specialty ?? raw?.Specialty ?? '';
  const sub = raw?.skillsetCombination ?? raw?.SkillsetCombination ?? '';
  const existing = raw?.skillset ?? raw?.Skillset ?? '';
  if (existing) return existing;
  const parts = [discipline, specialty, sub].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
}

function normalizePlacement(raw: any): IRecruitmentRetentionPlacement {
  const placementId = raw.placementID ?? raw.placementId ?? raw.PlacementId ?? raw.PlacementID;
  const facilityId = raw.facilityID ?? raw.facilityId ?? raw.FacilityId ?? raw.FacilityID;
  const brandId = raw.brandID ?? raw.brandId ?? raw.BrandId ?? raw.BrandID;
  return {
    placementId,
    candidateId: raw.travelerID ?? raw.travelerId ?? raw.candidateId ?? raw.CandidateId ?? raw.TravelerID,
    candidateName: buildCandidateName(raw),
    skillset: buildSkillset(raw),
    discipline: raw?.discipline ?? raw?.Discipline ?? '',
    specialty: raw?.specialty ?? raw?.Specialty ?? '',
    brand: raw.brand ?? raw.Brand ?? '-',
    brandId,
    city: raw.city ?? raw.City ?? '-',
    state: raw.facilityState ?? raw.state ?? raw.State ?? raw.FacilityState ?? '-',
    facilityId,
    availableToStart: raw.availableToStart ?? raw.AvailableToStart ?? raw.availableToStartDate ?? raw.AvailableToStartDate ?? '',
    lastContacted: raw.recruiterLastAttempt ?? raw.lastContacted ?? raw.LastContacted ?? raw.RecruiterLastAttempt ?? '',
    candidateStatus: raw.candidateStatus ?? raw.CandidateStatus ?? '-',
    placementStatus: raw.placementStatus ?? raw.PlacementStatus ?? '-',
    placementStatusId: raw.placementStatusId ?? raw.PlacementStatusId ?? undefined,
    placementActivityTypeID:
      raw.placementActivityTypeID ??
      raw.PlacementActivityTypeID ??
      raw.placementActivityTypeId ??
      raw.activityTypeId ??
      raw.ActivityTypeId ??
      undefined,
    placementActivityStatusID:
      raw.placementActivityStatusID ??
      raw.PlacementActivityStatusID ??
      raw.placementActivityStatusId ??
      raw.activityStatusId ??
      raw.ActivityStatusId ??
      undefined,
    rebooked: (raw.rebooked ?? raw.Rebooked ?? 'N') as 'Y' | 'N',
    currentEndDate: raw.currentEndDate ?? raw.CurrentEndDate ?? '',
    lastApplicationDate: raw.lastApplicationDate ?? raw.LastApplicationDate ?? '',
    callBackDate: raw.callBackDate ?? raw.CallBackDate ?? null,
    recruiterLastAttempt: raw.recruiterLastAttempt ?? raw.RecruiterLastAttempt ?? '',
    availableToStartDate: raw.availableToStart ?? raw.availableToStartDate ?? raw.AvailableToStart ?? raw.AvailableToStartDate ?? '',
    ...raw,
    placementId,
    facilityId,
    brandId,
    endDateModEffective: raw.endDateModEffective ?? raw.EndDateModEffective ?? null,
  };
}

function mapApiToPlacements(data: any): IRecruitmentRetentionPlacement[] {
  const items =
    data?.placements ??
    data?.data ??
    data?.items ??
    data?.Items ?? // .NET APIs often use PascalCase
    (Array.isArray(data) ? data : []);
  const arr = Array.isArray(items) ? items : [];
  return arr.map(normalizePlacement);
}

export function* fetchRecruitmentRetention(
  action: PayloadAction<{ recruiterId: number; startDate: string; endDate: string }>,
) {
  if (recruitmentRetentionCancelSource) {
    recruitmentRetentionCancelSource.cancel('Request cancelled - filter or reset changed');
  }
  recruitmentRetentionCancelSource = axios.CancelToken.source();
  const placementService: PlacementService = yield getContext('placementService');
  yield put(myCandidatesActions.resetRecruitmentRetentionFetch());
  try {
    const { recruiterId, startDate, endDate } = action.payload;
    const response = yield call(
      TrackWrapper,
      placementService.getRecruitmentRetention,
      { recruiterId, startDate, endDate },
      { cancelToken: recruitmentRetentionCancelSource.token },
    );

    if (httpSuccess(response?.status)) {
      const rawPlacements = mapApiToPlacements(response?.data ?? {});
      const fyStartYear = getFiscalYearStartFromDate(startDate);
      const barChartData: IRetentionBarChartMonth[] = computeBarChartData(rawPlacements, fyStartYear);
      const quickviewMetrics: IQuickviewMetric[] = computeQuickviewMetrics(rawPlacements);

      yield put(
        myCandidatesActions.setRecruitmentRetentionData({
          placements: rawPlacements,
          barChartData,
          quickviewMetrics,
        }),
      );
      yield put(myCandidatesActions.setRecruiterId(recruiterId));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchRecruitmentRetention',
        area: 'src/app/components/RecruiterWorkDesk/MyCandidatesDesk/MyCandidates.saga.ts',
      },
    });
  }
}

export function* myCandidatesSaga() {
  yield takeLatest(myCandidatesActions.fetchRecruiterOptionsAction.type, fetchRecruiterOptions);
  yield takeLatest(myCandidatesActions.fetchRecruitmentRetentionAction.type, fetchRecruitmentRetention);
}
